// Compact "Your channel today" widget for the Dashboard.
// Pulls already-cached stats from /integrations/status so it stays fast.
// Hides itself when nothing is connected — no empty-state noise on the home page.
import { useEffect, useState } from "react";
import { Youtube, Instagram, RotateCw, ArrowRight, Loader2 } from "lucide-react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { client } from "@/lib/api";

const INK_55 = "var(--ink-55)";
const SEAL = "var(--seal)";

const PROVIDER_META = {
  youtube: {
    name: "YouTube",
    icon: Youtube,
    accent: "#FF0033",
    primaryStat: { key: "subscribers", label: "subscribers" },
    secondaryStats: [
      { key: "views", label: "views" },
      { key: "videos", label: "videos" },
    ],
  },
  instagram: {
    name: "Instagram",
    icon: Instagram,
    accent: "#E1306C",
    primaryStat: { key: "followers", label: "followers" },
    secondaryStats: [
      { key: "media", label: "posts" },
    ],
  },
};

function formatCount(n) {
  if (n == null) return "—";
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(1)}K`;
  return String(n);
}

function relativeTime(iso) {
  if (!iso) return "never";
  const then = new Date(iso).getTime();
  const diff = Date.now() - then;
  const mins = Math.round(diff / 60000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.round(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.round(hrs / 24);
  return `${days}d ago`;
}

export default function ChannelWidget() {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(null);

  const load = async () => {
    try {
      const r = await client.get("/integrations/status");
      setStatus(r.data);
    } catch { /* widget is non-critical, fail quietly */ }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const sync = async (provider) => {
    setSyncing(provider);
    try {
      await client.post(`/integrations/${provider}/sync`);
      toast.success(`${PROVIDER_META[provider].name} stats refreshed`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Sync failed");
    } finally {
      setSyncing(null);
    }
  };

  if (loading) return null;

  const connected = ["youtube", "instagram"]
    .map((p) => ({ provider: p, ...status?.[p] }))
    .filter((p) => p?.connected && p?.account?.stats);

  // Don't show the widget at all when nothing is connected — keep the dashboard
  // calm. The Connect prompt already lives in Settings + Analytics.
  if (connected.length === 0) {
    // Show a soft prompt instead when at least one is configured (so users
    // discover the feature) — but only if NOT yet connected.
    const anyConfigured = ["youtube", "instagram"].some((p) => status?.[p]?.configured);
    if (!anyConfigured) return null;
    return (
      <Link
        to="/settings"
        className="block rounded-xl p-4 transition hover:opacity-90"
        style={{ background: "var(--surface)", border: "1px dashed var(--ink-22)" }}
        data-testid="channel-widget-cta"
      >
        <div className="flex items-center justify-between gap-3">
          <div>
            <div className="eyebrow mb-1">Your channel</div>
            <div className="text-sm" style={{ color: "var(--ink-72)" }}>
              Connect YouTube to see live subscriber + view stats here.
            </div>
          </div>
          <ArrowRight size={14} style={{ color: SEAL }} />
        </div>
      </Link>
    );
  }

  return (
    <div className="space-y-3" data-testid="channel-widget">
      {connected.map(({ provider, account }) => {
        const m = PROVIDER_META[provider];
        const Icon = m.icon;
        const primary = account.stats?.[m.primaryStat.key];
        return (
          <div
            key={provider}
            className="rounded-xl p-4 flex items-center gap-4"
            style={{ background: "var(--surface)", border: "1px solid var(--ink-22)" }}
            data-testid={`channel-widget-${provider}`}
          >
            <div
              className="w-11 h-11 rounded-full flex items-center justify-center flex-shrink-0"
              style={{ background: `${m.accent}14`, border: `1px solid ${m.accent}44` }}
            >
              <Icon size={18} style={{ color: m.accent }} />
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-baseline gap-2">
                <div className="font-heading text-2xl leading-none" style={{ fontVariationSettings: "'opsz' 48, 'wght' 500" }}>
                  {formatCount(primary)}
                </div>
                <div className="text-xs" style={{ color: INK_55 }}>{m.primaryStat.label}</div>
              </div>
              <div className="text-[11px] mt-1.5 flex flex-wrap items-center gap-x-3 gap-y-1" style={{ color: INK_55 }}>
                <span className="truncate max-w-[170px]" style={{ color: "var(--ink-72)" }}>
                  {account.account_label}
                </span>
                {m.secondaryStats.map((s) => (
                  <span key={s.key}>
                    <span style={{ color: "var(--ink-72)" }}>{formatCount(account.stats?.[s.key])}</span> {s.label}
                  </span>
                ))}
                <span>· synced {relativeTime(account.last_synced_at)}</span>
              </div>
            </div>
            <button
              onClick={() => sync(provider)}
              disabled={syncing === provider}
              className="btn-ghost text-xs flex-shrink-0"
              title="Refresh stats"
              data-testid={`channel-widget-${provider}-sync`}
              style={{ touchAction: "manipulation" }}
            >
              {syncing === provider ? <Loader2 size={12} className="animate-spin" /> : <RotateCw size={12} />}
            </button>
          </div>
        );
      })}
    </div>
  );
}
